import {
  deckJoinSchema,
  deckPageSchema,
  deckPdfUploadSchema,
  deckSummarySchema,
  type DeckPage,
  type DeckSummary,
  type ReferenceDocumentSummary,
} from '../../shared/contracts/deck-api.ts';
import type { DeckScope } from '../../shared/deck.ts';
import { ApiResponseError, parseJsonResponse } from './api-client.ts';

export type {
  DeckPage,
  DeckSummary,
  ReferenceDocumentSummary,
} from '../../shared/contracts/deck-api.ts';
export { ApiResponseError } from './api-client.ts';

export function deckPath(slug: string, scope: DeckScope = {}): string {
  let path = `/deck/${encodeURIComponent(slug)}`;
  if (scope.sectionSlug)
    path += `/section/${encodeURIComponent(scope.sectionSlug)}`;
  if (scope.unitSlug) path += `/unit/${encodeURIComponent(scope.unitSlug)}`;
  if (scope.topicSlug) path += `/topic/${encodeURIComponent(scope.topicSlug)}`;
  return path;
}

export async function getDecks(
  fetcher: typeof fetch = fetch,
  signal?: AbortSignal,
): Promise<DeckSummary[]> {
  return parseJsonResponse(
    await fetcher('/api/decks', { signal }),
    deckSummarySchema.array(),
    'Deck request failed',
  );
}

export async function uploadDeckPdf(
  deckId: string,
  file: File,
  fetcher: typeof fetch = fetch,
): Promise<{ pageCount: number; referenceDocumentId: string }> {
  const form = new FormData();
  form.set('file', file);
  return parseJsonResponse(
    await fetcher(`/api/decks/${encodeURIComponent(deckId)}/pdf`, {
      method: 'POST',
      body: form,
    }),
    deckPdfUploadSchema,
    'Deck PDF upload failed',
  );
}

export function referenceDocumentCopyUrl(
  deckId: string,
  documentId: string,
): string {
  return `/api/decks/${deckId}/reference-documents/${documentId}/copy`;
}

export function isSupportedPdfFile(file: File): boolean {
  return (
    file.type === 'application/pdf' ||
    file.name.trim().toLowerCase().endsWith('.pdf')
  );
}

export type SequentialUploadResult =
  | { file: File; status: 'success' }
  | { file: File; status: 'unsupported' }
  | { error: unknown; file: File; status: 'failed' };

export type SequentialUploadUpdate = {
  error?: unknown;
  file: File;
  index: number;
  status: 'uploading' | 'success' | 'unsupported' | 'failed';
};

export async function uploadDeckPdfFilesSequential(
  deckId: string,
  files: File[],
  upload: typeof uploadDeckPdf = uploadDeckPdf,
  onUpdate?: (update: SequentialUploadUpdate) => void | Promise<void>,
): Promise<SequentialUploadResult[]> {
  const results: SequentialUploadResult[] = [];
  for (const [index, file] of files.entries()) {
    if (!isSupportedPdfFile(file)) {
      await onUpdate?.({ file, index, status: 'unsupported' });
      results.push({ file, status: 'unsupported' });
      continue;
    }
    await onUpdate?.({ file, index, status: 'uploading' });
    try {
      await upload(deckId, file);
      await onUpdate?.({ file, index, status: 'success' });
      results.push({ file, status: 'success' });
    } catch (error) {
      await onUpdate?.({ error, file, index, status: 'failed' });
      results.push({ error, file, status: 'failed' });
    }
  }
  return results;
}

export function documentManagementUrl(
  deckSlug: string,
  documentId: string,
): string {
  return `/deck/${encodeURIComponent(deckSlug)}?document=${encodeURIComponent(documentId)}`;
}

export async function getDeckPage(
  slug: string,
  fetcher: typeof fetch = fetch,
  signal?: AbortSignal,
  scope?: DeckScope,
): Promise<DeckPage> {
  const params = new URLSearchParams();
  if (scope?.sectionSlug) params.set('section', scope.sectionSlug);
  if (scope?.unitSlug) params.set('unit', scope.unitSlug);
  if (scope?.topicSlug) params.set('topic', scope.topicSlug);
  const query = params.size ? `?${params.toString()}` : '';
  return parseJsonResponse(
    await fetcher(`/api/decks/${encodeURIComponent(slug)}${query}`, { signal }),
    deckPageSchema,
    'Deck request failed',
  );
}

export async function joinDeck(
  slug: string,
  fetcher: typeof fetch = fetch,
): Promise<{ joined: true; deckId: string }> {
  return parseJsonResponse(
    await fetcher(`/api/decks/${encodeURIComponent(slug)}/join`, {
      method: 'POST',
    }),
    deckJoinSchema,
    'Deck join failed',
  );
}
